// 斗地主 in the 牌桌 panel (public/cards.js mounts it; the rules: src/cards/doudizhu.ts). The felt with the three seats
// (地主 / 农民, cards left, 积分, whose turn with its clock), the 底牌 at the top (face down till there is a 地主), what
// each seat played last in front of it (or 不出), the 倍数; my hand big under it: tap or drag across cards to pick
// them; and my moves: 叫地主 (不叫 / 1分 / 2分 / 3分), then 不出 / 提示 / 出牌. When the deal ends every hand still
// held is shown and the score. Only what the office sent me is drawn: my own hand, and the others' once it's over.
import { CARD_W, CARD_H, drawCard, drawFelt, seatXY, nameBox, boxAt, outlined, sized, el, INK, CREAM, seatName } from '/cards-common.js';
import { t, tr, has } from '/i18n.js';

const W = 400, H = 270;
const ORDER = '3456789TJQKA2BR';
const rankOf = (c) => (c === 'BJ' ? 13 : c === 'RJ' ? 14 : ORDER.indexOf(c[0]));
/** A bid (0: 不叫, 1–3 分) in words. */
const bidText = (b) => t(`ddz.bid.${b}`);
/** 地主 / 农民 in words. */
const ROLE = { landlord: 'ddz.role.landlord', farmer: 'ddz.role.farmer' };
const HAND_K = 2, HW = 400, HH = CARD_H * HAND_K + 16, LIFT = 14;

export default {
  kind: 'doudizhu', get name() { return t('ddz.name'); }, icon: 'cards', seats: 3, min: 3, get blurb() { return t('ddz.blurb'); },
  mount(root, api) {
    let st = null, sel = new Set(), hintAt = 0, hintSig = '', drag = null, lastRound = null;
    const canvas = el('canvas', 'ctfelt ctdoudizhu-felt'), wrap = el('div', 'ctfeltwrap'); wrap.append(canvas);
    const handWrap = el('div', 'ctdoudizhu-handwrap'), handCanvas = el('canvas', 'ctdoudizhu-hand'); handWrap.append(handCanvas);
    const bidRow = el('div', 'gmbtns ctacts ctdoudizhu-acts');
    const bidBtns = [0, 1, 2, 3].map((b) => { const x = tr(el('button', b === 3 ? 'gmprimary' : ''), `ddz.bid.${b}`); x.onclick = () => api.send(`bid ${b}`); return x; });
    bidRow.append(...bidBtns);
    const playRow = el('div', 'gmbtns ctacts ctdoudizhu-acts');
    const passBtn = tr(el('button'), 'ddz.pass'), hintBtn = tr(el('button'), 'ddz.hint'), clearBtn = tr(el('button'), 'ddz.clear'), playBtn = tr(el('button', 'gmprimary'), 'ddz.play');
    playRow.append(passBtn, hintBtn, clearBtn, playBtn);
    const me2 = el('div', 'ctme'), log = el('div', 'ctlog');
    root.append(wrap, handWrap, bidRow, playRow, me2, log);

    const v = () => st?.view ?? {};
    const myHand = () => v().you?.hand ?? [];
    passBtn.onclick = () => { sel.clear(); api.send('pass'); };
    clearBtn.onclick = () => { sel.clear(); render(st); };
    hintBtn.onclick = () => {
      const plays = (st?.legal ?? []).filter((m) => m.startsWith('play '));
      if (!plays.length) { api.toast(t(v().last ? 'ddz.hint.none' : 'ddz.hint.empty')); return; }
      const ranks = plays[hintAt % plays.length].slice(5); hintAt++;
      sel = new Set(); const hand = myHand();
      for (const ch of ranks) { const c = hand.find((x) => !sel.has(x) && ORDER[rankOf(x)] === ch); if (c) sel.add(c); }
      render(st);
    };
    playBtn.onclick = () => {
      if (!sel.size) { api.toast(t('ddz.pickFirst')); return; }
      const cards = myHand().filter((c) => sel.has(c));
      // the cards themselves (jokers as B / R), or just their ranks when that's too long for one move
      let body = cards.map((c) => (c === 'BJ' ? 'B' : c === 'RJ' ? 'R' : c)).join('');
      if (body.length > 35) body = cards.map((c) => ORDER[rankOf(c)]).join('');
      api.send(`play ${body}`);
    };

    // ---- my hand: tap a card to pick it, or drag across several
    const layout = () => {
      const hand = myHand(), n = hand.length, cw = CARD_W * HAND_K;
      const step = n > 1 ? Math.min(cw * 0.62, (HW - 8 - cw) / (n - 1)) : 0, x0 = (HW - (cw + step * (n - 1))) / 2;
      return { hand, step, x0, cw };
    };
    function cardAt(e) {
      const r = handCanvas.getBoundingClientRect(), x = ((e.clientX - r.left) / r.width) * HW, y = ((e.clientY - r.top) / r.height) * HH;
      const { hand, step, x0, cw } = layout();
      // the topmost card under the pointer (later cards lie over earlier ones)
      for (let i = hand.length - 1; i >= 0; i--) {
        const cx = x0 + i * step, top = sel.has(hand[i]) ? 2 : 2 + LIFT;
        if (x >= cx && x <= cx + cw && y >= top - 2 && y <= top + CARD_H * HAND_K) return hand[i];
      }
      return null;
    }
    handCanvas.addEventListener('pointerdown', (e) => {
      const c = cardAt(e); if (!c) return;
      e.preventDefault(); handCanvas.setPointerCapture?.(e.pointerId);
      drag = { on: !sel.has(c), seen: new Set([c]) };
      if (drag.on) sel.add(c); else sel.delete(c);
      drawHand();
    });
    handCanvas.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const c = cardAt(e); if (!c || drag.seen.has(c)) return;
      drag.seen.add(c); if (drag.on) sel.add(c); else sel.delete(c);
      drawHand();
    });
    const endDrag = () => { if (drag) { drag = null; render(st); } };
    handCanvas.addEventListener('pointerup', endDrag); handCanvas.addEventListener('pointercancel', endDrag);

    const bottom = () => (api.mySeat() >= 0 ? api.mySeat() : 0);
    const at = (i) => seatXY(i, 3, bottom(), W, H);
    canvas.addEventListener('click', (e) => { if (!st) return; const i = boxAt(canvas, e, W, H, 3, at); if (i >= 0 && !st.seats[i] && api.mySeat() < 0) api.sitAt(i); });

    /** Cards in a fan from (x, y), `k` times size, at most `per` in a row; align: 'l' | 'c' | 'r'. */
    function fan(g, cards, x, y, align, k = 1, per = 10, dim = false) {
      const step = 11 * k, rows = [];
      for (let i = 0; i < cards.length; i += per) rows.push(cards.slice(i, i + per));
      rows.forEach((row, ri) => {
        const w = CARD_W * k + step * (row.length - 1), x0 = align === 'c' ? x - w / 2 : align === 'r' ? x - w : x;
        row.forEach((c, i) => drawCard(g, c, x0 + i * step, y + ri * 18 * k, k, dim));
      });
    }
    function draw() {
      const g = sized(canvas, W, H);
      drawFelt(g, W, H);
      if (!st) return;
      const V = v(), res = V.result, ll = V.landlord ?? -1, mine = api.mySeat(), live = V.live;
      // the 底牌 and the 倍数
      const bottomCards = V.bottom?.length ? V.bottom : V.bottomCount ? ['back', 'back', 'back'] : [];
      bottomCards.forEach((c, i) => drawCard(g, c, W / 2 - 40 + i * 28, 46, 1));
      if (bottomCards.length && !res) outlined(g, V.phase === 'bid' ? t('ddz.bottom.bid', { n: V.bid || 0 }) : t('ddz.mult', { base: V.base, mult: res?.mult || V.mult }), W / 2, 92);
      for (let i = 0; i < 3; i++) {
        const p = st.seats[i], [x, y] = at(i), acting = live && V.cur === i;
        const role = ll < 0 ? '' : t(ROLE[i === ll ? 'landlord' : 'farmer']);
        let line = '';
        if (p) {
          if (res?.delta && res.win) line = t('ddz.line.delta', { delta: `${res.delta[i] > 0 ? '+' : ''}${res.delta[i]}`, score: V.score[i] });
          else if (V.phase === 'bid' && V.bids?.[i] != null) line = bidText(V.bids[i]);
          else if (live) line = role ? t('ddz.line.roleCards', { role, n: V.counts[i] }) : t('ddz.line.cards', { n: V.counts[i] });
          else line = t('ddz.line.score', { n: V.score?.[i] ?? 0 });
        }
        nameBox(g, x, y, { name: seatName(p, i), empty: t('ddz.seat.empty'), line, mine: i === mine, acting, won: !!res?.delta && res.delta[i] > 0,
          clock: acting && st.moveMs ? (api.deadline() - Date.now()) / st.moveMs : null, badge: i === ll ? t('ddz.badge') : null });
        // what the seat played last (or 不出); at the end, the hand still held
        const sh = V.shown?.[i], left = res?.hands?.[i];
        const bx = i === bottom() ? W / 2 : x < W / 2 ? x + 50 : x - 50, by = i === bottom() ? y - 64 : y + 12, al = i === bottom() ? 'c' : x < W / 2 ? 'l' : 'r';
        if (res && left?.length && i !== mine) fan(g, left, bx, by - 6, al, 1, 9);
        else if (sh === 'pass') outlined(g, t('ddz.pass'), bx, by + 12, al === 'l' ? 'left' : al === 'r' ? 'right' : 'center');
        else if (Array.isArray(sh) && sh.length) fan(g, sh, bx, by - (i === bottom() ? 0 : 6), al, 1, i === bottom() ? 14 : 9);
        // a stack of backs for the cards they hold
        if (p && live && i !== mine && V.counts[i]) { const cx = x < W / 2 ? x - 54 : x + 30; drawCard(g, 'back', cx, y - 17, 1); outlined(g, String(V.counts[i]), cx + 12, y + 24); }
      }
      if (res) {
        const text = res.win ? t('ddz.result.win', { left: res.why === 'left' ? 'yes' : 'no', side: res.win, spring: res.spring || 'none' }) : t(res.why === 'nobid' ? 'ddz.result.nobid' : 'ddz.result.void');
        g.font = 'bold 14px system-ui'; const w = g.measureText(text).width + 24;
        g.fillStyle = CREAM; g.strokeStyle = INK; g.lineWidth = 2; g.beginPath(); g.roundRect?.(W / 2 - w / 2, H / 2 - 4, w, 26, 8) ?? g.rect(W / 2 - w / 2, H / 2 - 4, w, 26); g.fill(); g.stroke();
        g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, W / 2, H / 2 + 9);
      }
    }
    function drawHand() {
      const g = sized(handCanvas, HW, HH), { hand, step, x0 } = layout();
      hand.forEach((c, i) => drawCard(g, c, x0 + i * step, sel.has(c) ? 2 : 2 + LIFT, HAND_K));
    }
    function render(s) {
      st = s;
      const V = v(), myTurn = api.myTurn(), hand = myHand();
      // a new deal starts with nothing picked; picks of cards no longer held go; a new turn starts the hints over
      if (s?.round !== lastRound) { lastRound = s?.round; sel.clear(); }
      for (const c of [...sel]) if (!hand.includes(c)) sel.delete(c);
      const sig = `${s?.round}:${s?.view?.log?.length}:${V.cur}`;
      if (sig !== hintSig) { hintSig = sig; hintAt = 0; }
      draw();
      handWrap.style.display = hand.length ? '' : 'none';
      if (hand.length) drawHand();
      bidRow.style.display = myTurn && V.phase === 'bid' ? '' : 'none';
      bidBtns.forEach((b, i) => { b.disabled = i > 0 && i <= (V.bid || 0); });
      playRow.style.display = myTurn && V.phase === 'play' ? '' : 'none';
      passBtn.style.display = V.last ? '' : 'none';
      clearBtn.style.display = sel.size ? '' : 'none';
      playBtn.disabled = !sel.size;
      const mine = api.mySeat();
      me2.textContent = mine >= 0 && V.deal ? (V.landlord >= 0 ? t('ddz.me.role', { n: V.score?.[mine] ?? 0, role: V.landlord === mine ? 'landlord' : 'farmer' }) : t('ddz.me.points', { n: V.score?.[mine] ?? 0 })) : '';
      me2.style.display = me2.textContent ? '' : 'none';
      log.replaceChildren(...(V.log ?? []).slice(-8).map((l) => el('div', '', l.cards?.length ? t('ddz.log.cards', { name: api.name(l.seat), a: actText(l), cards: l.cards.map(short).join(' ') }) : t('ddz.log', { name: api.name(l.seat), a: actText(l) }))));
      log.style.display = log.childNodes.length ? '' : 'none';
      log.scrollTop = log.scrollHeight;
    }
    /** A move in the log from its code (l.k: a bid, a kind of play, …); an older office's: its words (l.a). */
    const actText = (l) => (l.k === 'bid' && Number.isInteger(l.n) ? bidText(l.n) : l.k === 'play' && has(`ddz.type.${l.type}`) ? t(`ddz.type.${l.type}`) : l.k && has(`ddz.act.${l.k}`) ? t(`ddz.act.${l.k}`) : l.a);
    const short = (c) => (c === 'BJ' || c === 'RJ' ? t(`cards.card.${c}`) : c[0] === 'T' ? '10' : c[0]);
    return {
      render,
      tick() { if (st?.phase === 'playing') draw(); },
      status(s) {
        const V = s.view ?? {}, n = (i) => api.name(i), mineTurn = api.myTurn();
        const r = V.result;
        if (s.phase === 'over' && r) return r.win ? t('ddz.status.won', { left: r.why === 'left' ? 'yes' : 'no', side: r.win, name: n(V.landlord), base: r.base, mult: r.mult }) : t(r.why === 'nobid' ? 'ddz.result.nobid' : 'ddz.result.void');
        if (s.phase !== 'playing' || !V.live) return null;
        if (V.phase === 'bid') return mineTurn ? t('ddz.status.bidYou', { n: V.bid || 0 }) : t('ddz.status.bidding', { name: n(V.cur) });
        // the kind of play from its code (ddz.type.*); typeName (the office's words) for a kind this page doesn't know
        if (mineTurn) return V.last ? t('ddz.status.beat', { name: n(V.last.seat), type: has(`ddz.type.${V.last.type}`) ? t(`ddz.type.${V.last.type}`) : V.last.typeName }) : t('ddz.status.lead');
        return t('ddz.status.playing', { name: n(V.cur) });
      },
      // 叫地主 not over: the deal is void; after that, leaving loses (逃跑算输)
      leaveWarn(s) { return s?.phase === 'playing' && s.view?.live ? t(s.view.phase === 'play' ? 'ddz.leave.lose' : 'ddz.leave.void') : null; },
    };
  },
};
